import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { usePersistStore, useStore } from "../store";
import { UserService } from "../services";

interface ArtistDescriptionModalProps {
  show: boolean;
  onHide: () => void;
}

const MAX_LENGTH = 1000;

const ArtistDescriptionModal: React.FC<ArtistDescriptionModalProps> = ({
  show,
  onHide,
}) => {
  const { token, user, setUser } = usePersistStore((s: any) => s);
  const { setSuccessMessage } = useStore((s: any) => s);
  const [text, setText] = useState<string>("");
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) setText(user?.description || "");
  }, [show, user?.description]);

  const handleSubmit = async (e: any) => {
    e?.preventDefault();
    setValidationMessage("");
    if (!text || !text.trim())
      return setValidationMessage("Please provide your description.");
    if (text.length > MAX_LENGTH)
      return setValidationMessage(
        `Description must be ${MAX_LENGTH} characters or fewer.`
      );

    setSubmitting(true);
    let result = await UserService.updateDescription(token, {
      description: text,
    });
    setSubmitting(false);
    if (result?.error) {
      setValidationMessage(result?.error);
      return;
    }

    setSuccessMessage("Artist description updated.");
    setUser(result?.data);
    onHide();
  };

  const handleClose = () => {
    setValidationMessage("");
    onHide();
  };

  return (
    <Modal
      className="placeBidModal artistDescModal"
      show={show}
      onHide={handleClose}
      aria-labelledby="contained-modal-title-vcenter"
      size="lg"
      centered
    >
      <Modal.Header closeButton className="placeBidModal__header">
        <Modal.Title className="placeBidModal__heading">
          Artist description
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="placeBidModal__body">
        <div className="updatePasswordModal__intro">
          <span className="updatePasswordModal__iconWrap">
            <i className="bi bi-stars" aria-hidden="true" />
          </span>
          <div>
            <h4 className="updatePasswordModal__title">
              Tell collectors who you are
            </h4>
            <p className="updatePasswordModal__hint">
              Share your background, inspirations, and the mediums you work
              with. This shows on your seller profile.
            </p>
          </div>
        </div>

        <Form.Group className="field mb-0">
          <label className="field__label">
            Description<span className="field__required">*</span>
          </label>
          <Form.Control
            as="textarea"
            rows={10}
            placeholder="I am a self-taught oil painter from the Pacific Northwest. My work explores the boundary between memory and dream, often featuring..."
            value={text}
            onChange={(e: any) => setText(e?.target?.value)}
            maxLength={MAX_LENGTH + 200 /* soft cap, hard-validated above */}
          />
          <div className="field__meta">
            <span className="field__hint">
              Aim for 100–500 characters.
            </span>
            <span
              className={`field__counter ${
                text.length > MAX_LENGTH ? "field__counter--over" : ""
              }`}
            >
              {text.length}/{MAX_LENGTH}
            </span>
          </div>
        </Form.Group>

        {validationMessage && (
          <div className="placeBidModal__error">
            <i className="bi bi-x-circle" aria-hidden="true" />
            <span>{validationMessage}</span>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="placeBidModal__footer">
        <button
          type="button"
          className="placeBidModal__cancel"
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="placeBidModal__submit"
          onClick={handleSubmit}
          disabled={submitting}
        >
          <i className="bi bi-check2-circle" aria-hidden="true" />
          <span>Save description</span>
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ArtistDescriptionModal;
